/*
 * Chat Sidebar Component
 * Features: Unified sidebar with tabs for chat sessions and prompt library, collapsible panel
 * Logic: Tab navigation, session list sorted by updatedAt desc, prompt list, CRUD operations, active state management
 * Changes:
 * - Initial implementation combining session and prompt management
 * - Updated all property references from PascalCase to camelCase (id, title, messageCount, lastMessageAt, etc.)
 * - Added event bus integration for prompt refresh notifications
 * - Fixed session click handler: Entire session card is now clickable for better UX
 * - Added stopPropagation to edit/delete buttons to prevent session selection when clicking action buttons
 * - Removed favorite icon and toggle functionality from prompt list for simplified UI
 * - Added collapse/expand functionality similar to Kanban page's left panel
 * - Sessions now sorted by updatedAt in descending order (most recent first)
 * - Moved edit/delete buttons to align beside session title on the right
 */

import { FC, useState, useEffect, useCallback } from 'react';
import { chatSessionService, ChatSession } from '../services/chatSessionService';
import { promptService, Prompt } from '../services/promptService';
import { THEME_CLASSES } from '../constants/themeConstants';
import { useEventBus } from '../context/EventBusContext';
import { EVENTS } from '../constants/eventConstants';

interface ChatSidebarProps {
  activeSessionId?: number;
  onSessionSelect: (sessionId: number) => void;
  onPromptSelect: (promptContent: string) => void;
  isLoading?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean, options?: { persist?: boolean }) => void;
  refreshToken?: number;
}

type TabType = 'sessions' | 'prompts';

const ChatSidebar: FC<ChatSidebarProps> = ({
  activeSessionId,
  onSessionSelect,
  onPromptSelect,
  isLoading = false,
  collapsed,
  onCollapsedChange,
  refreshToken,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('sessions');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { subscribe, unsubscribe } = useEventBus();

  // Load sessions
  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await chatSessionService.getSessions();
      // Sort by updatedAt in descending order (most recent first)
      const sortedData = [...data].sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setSessions(sortedData);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load prompts
  const loadPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await promptService.getPrompts();
      const sortedPrompts = [...data].sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setPrompts(sortedPrompts);
    } catch (error) {
      console.error('Failed to load prompts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    } else {
      loadPrompts();
    }
  }, [activeTab, refreshToken, loadSessions, loadPrompts]);

  useEffect(() => {
    const handlePromptRefresh = () => {
      setIsCollapsed(false);
      onCollapsedChange?.(false, { persist: false });
      setActiveTab('prompts');
      loadPrompts();
    };

    subscribe(EVENTS.PROMPTS_REFRESH, handlePromptRefresh);

    return () => {
      unsubscribe(EVENTS.PROMPTS_REFRESH, handlePromptRefresh);
    };
  }, [loadPrompts, onCollapsedChange, subscribe, unsubscribe]);

  useEffect(() => {
    if (typeof collapsed === 'boolean') {
      setIsCollapsed(collapsed);
    }
  }, [collapsed]);

  // Handle session deletion
  const handleDeleteSession = async (sessionId: number) => {
    if (!confirm('Delete this chat session?')) return;

    try {
      const result = await chatSessionService.deleteSession(sessionId);
      await loadSessions();

      // If deleted session was active, select new active session
      if (result.newActiveSessionId) {
        onSessionSelect(result.newActiveSessionId);
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  // Handle session title editing
  const handleStartEdit = (session: ChatSession) => {
    setEditingSessionId(session.id);
    setEditingTitle(session.title);
  };

  const handleSaveEdit = async (sessionId: number) => {
    try {
      await chatSessionService.updateSession(sessionId, editingTitle);
      await loadSessions();
      setEditingSessionId(null);
    } catch (error) {
      console.error('Failed to update session:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  // Handle prompt actions
  const handleDeletePrompt = async (promptId: number) => {
    if (!confirm('Delete this prompt?')) return;

    try {
      await promptService.deletePrompt(promptId);
      await loadPrompts();
    } catch (error) {
      console.error('Failed to delete prompt:', error);
    }
  };

  const handleUsePrompt = async (prompt: Prompt) => {
    try {
      await promptService.incrementUseCount(prompt.id);
      onPromptSelect(prompt.promptContent);
    } catch (error) {
      console.error('Failed to use prompt:', error);
    }
  };

  return (
    <div className={`flex flex-col h-full bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-full'}`}>
      {isCollapsed ? (
        /* Collapsed State - Only show expand button */
        <div className="p-2">
          <button
            onClick={() => {
              setIsCollapsed(false);
              onCollapsedChange?.(false, { persist: true });
            }}
            className={`w-full p-2 rounded-lg ${THEME_CLASSES.hover.background} transition-colors flex items-center justify-center`}
            title="Expand sidebar"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 ${THEME_CLASSES.text.secondary} rotate-180`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      ) : (
        <>
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('sessions')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'sessions'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              Sessions
            </button>
            <button
              onClick={() => setActiveTab('prompts')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'prompts'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              Prompts
            </button>
            <button
              onClick={() => {
                setIsCollapsed(true);
                onCollapsedChange?.(true, { persist: true });
              }}
              className={`p-3 ${THEME_CLASSES.hover.background} transition-colors flex items-center justify-center`}
              title="Collapse sidebar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'sessions' ? (
              <div className="p-2">
                <div className="flex justify-end mb-2">
                </div>
                {/* Sessions List */}
                {loading ? (
                  <div className="text-center py-4 text-gray-500">Loading...</div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">No chat sessions</div>
                ) : (
                  <div className="space-y-1">
                    {sessions.map(session => (
                      <div
                        key={session.id}
                        onClick={() => {
                          if (editingSessionId !== session.id && !isLoading) {
                            onSessionSelect(session.id);
                          }
                        }}
                        className={`group p-3 rounded-md transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${session.id === activeSessionId
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                      >
                        {editingSessionId === session.id ? (
                          <div className="flex flex-col gap-2">
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              className={THEME_CLASSES.input + ' text-sm'}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit(session.id);
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveEdit(session.id);
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded"
                              >
                                Save
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                                  {session.title}
                                </div>
                              </div>
                              <div className="flex gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEdit(session);
                                  }}
                                  className="p-1 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                                  title="Rename"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSession(session.id);
                                  }}
                                  className="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {session.messageCount} messages
                              {session.lastMessageAt && (
                                <span className="ml-2">
                                  {new Date(session.lastMessageAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-2">
                {/* Prompts List */}
                {loading ? (
                  <div className="text-center py-4 text-gray-500">Loading...</div>
                ) : prompts.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">No prompts saved</div>
                ) : (
                  <div className="space-y-2">
                    {prompts.map(prompt => (
                      <div
                        key={prompt.id}
                        className="group p-3 rounded-md bg-white dark:bg-gray-700 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => handleUsePrompt(prompt)}
                          >
                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                              {prompt.title}
                            </div>
                            {prompt.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {prompt.description}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              {prompt.category && (
                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-600 rounded">
                                  {prompt.category}
                                </span>
                              )}
                              <span>Used {prompt.useCount} times</span>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePrompt(prompt.id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatSidebar;
